import React from "react";
import { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import axios from 'axios';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import Button from "@material-ui/core/Button";
import * as enums from '../../../../../utils/EnumMapping';
import { fmt } from '../../../../../utils/Util';

import ProjectPhase from "./ProjectPhase";

export default function ProjectDetail(props) {
    const [data, setData] = useState();
    const [projectPhases, setProjectPhases] = useState();
    const [mapSrc, setMapSrc] = useState();
    const [checkLoading, setCheckLoading] = useState(false);

    const callApi = useCallback(async () => {
        const response = await axios.get(`/api/projectdetail/${props.match.params.id}`);
        return response;
    }, [props])

    const fetchProjectPhases = useCallback(async () => {
        const response = await axios.get('/api/projectphasestates');
        return response;
    }, [])

    const fetchLatLon = useCallback(async (address) => {
        // console.log(address);
        const response = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`);
        return response;
    }, [])

    useEffect(function () {
        callApi()
            .then(res => {
                setData(res.data);
                // after we have project data, try to resolve lat/lon from the address
                const address = res?.data?.project?.location;
                if (address) {
                    fetchLatLon(address)
                        .then(locRes => {
                            // console.log(locRes);
                            if (locRes && Array.isArray(locRes.data) && locRes.data.length) {
                                const { lat, lon } = locRes.data[0];
                                setMapSrc(`https://www.google.com/maps?q=${lat},${lon}&hl=hu&z=14&output=embed`);
                            }
                        })
                        .catch(err => console.log('fetchLatLon failed', err));
                }
            })
            .catch(err => console.log(err));
    }, [callApi]);

    useEffect(function () {
        fetchProjectPhases()
            .then(res => setProjectPhases(res.data))
            .catch(err => console.log(err));
    }, [fetchProjectPhases]);

    useEffect(() => {
        const handler = async (e) => {
            const { privateId } = (e && e.detail) || {};
            try {
                if (typeof callApi === "function") {
                    const res = await callApi();
                    setData(res.data);
                    // request ProjectPhase to reopen the same accordion item
                    if (privateId) {
                        // small timeout to ensure state updated/rendered before reopen event
                        setTimeout(() => {
                            window.dispatchEvent(new CustomEvent("projectphase:reopen", { detail: { privateId } }));
                        }, 50);
                    }
                }
            } catch (err) {
                console.error("Refresh after nextApplied failed", err);
            }
        };
        window.addEventListener("projectphase:nextApplied", handler);
        return () => window.removeEventListener("projectphase:nextApplied", handler);
    }, [callApi]);

    useEffect(() => {
        const handler = async (e) => {
            try {
                if (typeof callApi === "function") {
                    const res = await callApi();
                    setData(res.data);
                }
            } catch (err) {
                console.error("Refresh after delete failed", err);
            }
        };
        window.addEventListener("projectphase:deleted", handler);
        return () => window.removeEventListener("projectphase:deleted", handler);
    }, [callApi]);

    useEffect(function () {

    });

    const getClients = () => {
        return data?.project?.customers?.map(c => c.name).join(", ");
    }

    const handleCheckProjectPhase = async () => {
        if (!props.match.params.id) return;

        setCheckLoading(true);
        try {
            await axios.post(`/api/checkprojectphase/${props.match.params.id}`);
            // Refresh the project detail data
            const res = await callApi();
            setData(res.data);
        } catch (err) {
            console.error("Failed to check project phase", err);
        } finally {
            setCheckLoading(false);
        }
    };

    return (
        <div className="project-detail">
            <div className="breadcrumbs">
                <NavLink to="/project" activeClassName="active" className="project">Projektek</NavLink>
                <KeyboardArrowRightIcon fontSize="medium" />
                <span className="project-title">{data?.project?.location}</span>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleCheckProjectPhase}
                    disabled={checkLoading}
                    style={{ marginLeft: "auto" }}
                >
                    {checkLoading ? "Ellenőrzés..." : "Ütemek ellenőrzése"}
                </Button>
            </div>

            <div className="project-info">
                <div className="info-left">
                    <div className="info-grid">
                        <div className="info-item">
                            <div className="label">Munkaszám:</div>
                            <div className="value">{data?.project?.publicId || '—'}</div>
                        </div>
                        <div className="info-item">
                            <div className="label">Projekt rövid neve:</div>
                            <div className="value">{data?.project?.shortName || '—'}</div>
                        </div>

                        <div className="info-item">
                            <div className="label">Megrendelő(k):</div>
                            <div className="value">{getClients() || '—'}</div>
                        </div>
                        <div className="info-item">
                            <div className="label">Számlázás típusa:</div>
                            <div className="value">{data?.project?.billingType ? enums.getLabel(enums.billingTypes, data?.project?.billingType) : '—'}</div>
                        </div>

                        <div className="info-item">
                            <div className="label">Projekt kezdete:</div>
                            <div className="value">{fmt(data?.project?.startDate) || '—'}</div>
                        </div>
                        <div className="info-item">
                            <div className="label">Projekt vége:</div>
                            <div className="value">{fmt(data?.project?.endDate) || '—'}</div>
                        </div>

                        <div className="info-item">
                            <div className="label">Fizetési határidő:</div>
                            <div className="value">{data?.project?.paymentCommitmentInDays || '—'}</div>
                        </div>
                        <div className="info-item">
                            <div className="label">Megjegyzés:</div>
                            <div className="value">{data?.project?.comment || '—'}</div>
                        </div>
                    </div>
                </div>

                <aside className="info-right">
                    <div className="location-label">Helyszín:</div>
                    <h3 className="location-title">{data?.project?.location}</h3>
                    <div className="address">{data?.project?.address}</div>

                    {mapSrc ? (<>
                        <div className="map-container">
                            <iframe
                                src={mapSrc}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Google Map"
                            />
                        </div></>
                    ) : (
                        <div className="map-placeholder">Térkép betöltése...</div>
                    )}
                </aside>
            </div>

            <ProjectPhase title="Aktív ütemek" phases={data?.activeProjectPhases || []} nextPhases={projectPhases} />
            <ProjectPhase title="Lezárt ütemek" phases={data?.closedProjectPhases || []} />
        </div >
    );
}
